import { Show, createMemo, createSignal, onCleanup, onMount, useContext, For } from 'solid-js';
import { isServer } from 'solid-js/web';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { getImageServiceSrcPath } from '../image-component/image-component';
import { ProductImage } from './product-types';
import { ProductImageModal } from './product-image-modal';
import { NavControls } from '../../ui-components/nav-controls/nav-controls';
import { ZoomInCviIcon } from '../icons-library/zoom-in-cvi';

type ProductGalleryProps = {
    images: ProductImage[];
};

export const ProductGallery = (props: ProductGalleryProps) => {
    const { imagesServiceUrl, supportedImageFormats } = useContext(AppContext);
    const [modalIndex, setModalIndex] = createSignal<number | null>(null);
    const [scrollLeft, setScrollLeft] = createSignal(0);
    const [scrollMax, setScrollMax] = createSignal(0);
    let listRef: HTMLUListElement | undefined;

    const visibleImages = createMemo(() => {
        const valid = (props.images ?? []).filter((img) => Boolean(img?.file?.url));
        return [...valid].sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured));
    });

    const updateScrollState = () => {
        if (!listRef) {
            return;
        }
        setScrollLeft(listRef.scrollLeft);
        setScrollMax(listRef.scrollWidth - listRef.clientWidth);
    };

    const scrollByCard = (direction: 1 | -1) => {
        if (!listRef) {
            return;
        }
        const firstCard = listRef.querySelector('li');
        const step = firstCard ? firstCard.getBoundingClientRect().width : listRef.clientWidth;
        const gap = parseFloat(getComputedStyle(listRef).columnGap || '0');
        listRef.scrollBy({ left: direction * (step + gap), behavior: 'smooth' });
    };

    const isFirst = () => scrollLeft() <= 1;
    const isLast = () => scrollLeft() >= scrollMax() - 1;

    onMount(() => {
        if (isServer || !listRef) {
            return;
        }
        updateScrollState();
        listRef.addEventListener('scroll', updateScrollState, { passive: true });
        window.addEventListener('resize', updateScrollState);
    });

    onCleanup(() => {
        if (isServer || !listRef) {
            return;
        }
        listRef.removeEventListener('scroll', updateScrollState);
        window.removeEventListener('resize', updateScrollState);
    });

    return (
        <Show when={visibleImages().length > 0}>
            <div class="flex flex-col gap-cvi-5">
                <ul
                    ref={listRef}
                    class="flex list-none m-0 p-0 -mr-[var(--grid-column-gap-mobile)] tablet:mr-0 pr-[var(--grid-column-gap-mobile)] tablet:pr-0 gap-[var(--grid-column-gap-mobile)] tablet:gap-[var(--grid-column-gap-desktop)] tablet-original:gap-cvi-8 overflow-x-auto [scrollbar-width:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden @tablet:grid @tablet:grid-cols-2 @tablet:pl-0 @tablet:overflow-visible"
                >
                    <For each={visibleImages()}>
                        {(image, idx) => {
                            const src = getImageServiceSrcPath({
                                imagesServiceUrl,
                                supportedImageFormats,
                                src: image.file.url,
                                width: 500,
                                height: 'AUTO',
                            });
                            const alt = image.file?.alt;
                            return (
                                <li class="flex-[0_0_18.75rem] aspect-[4/3] pb-0">
                                    <button
                                        type="button"
                                        class="group relative block w-full h-full m-0 p-0 bg-transparent border border-cvi-border-default hover:border-cvi-border-strong rounded-cvi-lg overflow-hidden cursor-pointer transition-colors"
                                        onClick={() => setModalIndex(idx())}
                                    >
                                        <img
                                            class="w-full h-full object-contain object-center rounded-cvi-lg p-cvi-6"
                                            src={src}
                                            alt={alt || ''}
                                            {...(alt ? {} : { role: 'presentation' })}
                                        />
                                        <span class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            <span class="size-cvi-9 rounded-full bg-cvi-bg-default shadow-elevation-raised flex items-center justify-center text-cvi-fg-default">
                                                <ZoomInCviIcon ariaHidden size='small' />
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            );
                        }}
                    </For>
                </ul>
                <Show when={scrollMax() > 0}>
                    <div class="@tablet:hidden">
                        <NavControls
                            isFirst={isFirst()}
                            isLast={isLast()}
                            onPrev={() => scrollByCard(-1)}
                            onNext={() => scrollByCard(1)}
                        />
                    </div>
                </Show>
            </div>
            <Show when={modalIndex() !== null}>
                <ProductImageModal
                    images={visibleImages()}
                    initialIndex={modalIndex() as number}
                    onClose={() => setModalIndex(null)}
                />
            </Show>
        </Show>
    );
};
